import React, { useState, useEffect, useRef } from 'react';
import {
  View,
  ScrollView,
  StyleSheet,
  TextInput,
  TouchableOpacity,
  ActivityIndicator,
  KeyboardAvoidingView,
  Platform,
  Alert,
  Linking,
} from 'react-native';
import { useNavigation, useRoute, RouteProp } from '@react-navigation/native';
import { StackNavigationProp } from '@react-navigation/stack';
import { SafeAreaView, useSafeAreaInsets } from 'react-native-safe-area-context';
import { Ionicons } from '@expo/vector-icons';
import * as ImagePicker from 'expo-image-picker';
import { useTheme } from '../../theme/ThemeContext';
import { ClubsStackParamList } from '../../types/navigation';
import ThemedText from '../../components/common/ThemedText';
import IntroSegmentedControl, { IntroTab } from '../../components/clubs/IntroSegmentedControl';
import VideoSourcePicker, { VideoSource } from '../../components/feed/VideoSourcePicker';
import SelectedVideoPreview from '../../components/feed/SelectedVideoPreview';
import VideoProcessingDialog from '../../components/feed/VideoProcessingDialog';
import VideoErrorDialog from '../../components/feed/VideoErrorDialog';
import FeedVideoPlayer from '../../components/feed/FeedVideoPlayer';
import useKeyboardHeight from '../../hooks/useKeyboardHeight';
import FormFooterSpacer from '../../components/common/FormFooterSpacer';
import {
  createReferralRequest,
  createReferralVideoRequest,
  updateReferralRequest,
  updateReferralVideoRequest,
  getMyReferralRequests,
} from '../../lib/api/referrals';
import { showSuccessToast, showErrorToast } from '../../utils/toast';
import { inspectVideo, prepareVideoForUpload, cleanupOptimizedVideo } from '../../services/videoOptimization';
import { validateVideoDuration, formatDurationSeconds, formatFileSize } from '../../utils/videoValidation';
import { VIDEO_MAX_DURATION } from '../../config/env';

type CreateReferralNavigationProp = StackNavigationProp<ClubsStackParamList, 'CreateReferral'>;
type CreateReferralRouteProp = RouteProp<ClubsStackParamList, 'CreateReferral'>;

const countWords = (text: string) =>
  text.trim() === '' ? 0 : text.trim().split(/\s+/).length;

const CreateReferralScreen: React.FC = () => {
  const { theme } = useTheme();
  const navigation = useNavigation<CreateReferralNavigationProp>();
  const route = useRoute<CreateReferralRouteProp>();
  const { clubId, requestId } = route.params;
  const { bottom: bottomInset } = useSafeAreaInsets();

  const [activeTab, setActiveTab] = useState<IntroTab>('text');
  const [businessName, setBusinessName] = useState('');
  const [businessDetails, setBusinessDetails] = useState('');
  const [lookingFor, setLookingFor] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [loading, setLoading] = useState(false);
  const [headerHeight, setHeaderHeight] = useState(0);
  const { keyboardHeight } = useKeyboardHeight();

  const [videoUri, setVideoUri] = useState<string | null>(null);
  const [existingVideoUrl, setExistingVideoUrl] = useState<string | null>(null);
  const [videoMetaLabel, setVideoMetaLabel] = useState<string | null>(null);
  const [showVideoSourcePicker, setShowVideoSourcePicker] = useState(false);
  const [isPickingVideo, setIsPickingVideo] = useState(false);
  const [isProcessingVideo, setIsProcessingVideo] = useState(false);
  const [videoProcessingProgress, setVideoProcessingProgress] = useState(0);
  const [videoError, setVideoError] = useState<{
    visible: boolean;
    title: string;
    message: string;
    actionLabel?: string;
    onAction?: () => void;
  }>({
    visible: false,
    title: '',
    message: '',
  });

  const isMountedRef = useRef(true);
  const optimizedTempUriRef = useRef<string | null>(null);
  const cancelOptimizationRef = useRef<(() => void) | null>(null);

  const isEditMode = !!requestId;
  const hasLocalVideo = Boolean(videoUri && videoUri !== existingVideoUrl);
  const hasVideoReady = Boolean(videoUri || existingVideoUrl);

  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
      if (cancelOptimizationRef.current) {
        cancelOptimizationRef.current();
      }
      if (optimizedTempUriRef.current) {
        cleanupOptimizedVideo(optimizedTempUriRef.current);
      }
    };
  }, []);

  useEffect(() => {
    if (isEditMode && requestId) {
      loadReferralData();
    }
  }, [requestId]);

  const loadReferralData = async () => {
    try {
      setLoading(true);
      const requests = await getMyReferralRequests(clubId);
      const request = requests.find(r => r.id === requestId);
      if (request) {
        setBusinessName(request.businessName || '');
        setBusinessDetails(request.businessDetails || '');
        setLookingFor(request.lookingFor || '');
        if (request.videoUrl) {
          setExistingVideoUrl(request.videoUrl);
          setActiveTab('video');
        }
      }
    } catch (error: any) {
      showErrorToast(error.message || 'Failed to load referral request');
      navigation.goBack();
    } finally {
      setLoading(false);
    }
  };

  const showVideoErrorDialog = (
    title: string,
    message: string,
    action?: { label: string; onPress: () => void }
  ) => {
    setVideoError({
      visible: true,
      title,
      message,
      actionLabel: action?.label,
      onAction: action?.onPress,
    });
  };

  const handlePickVideo = async (source: VideoSource) => {
    setShowVideoSourcePicker(false);

    if (isPickingVideo || isProcessingVideo) {
      return;
    }

    setIsPickingVideo(true);

    try {
      if (source === 'camera') {
        const { status } = await ImagePicker.requestCameraPermissionsAsync();
        if (status !== 'granted') {
          showVideoErrorDialog(
            'Camera permission needed',
            'Enable Camera (and Microphone) in Settings to record a video.',
            Platform.OS === 'web'
              ? undefined
              : { label: 'Open Settings', onPress: () => Linking.openSettings() }
          );
          return;
        }
      }

      const options: ImagePicker.ImagePickerOptions = {
        mediaTypes: ImagePicker.MediaTypeOptions.Videos,
        allowsEditing: false,
        videoMaxDuration: VIDEO_MAX_DURATION,
        quality: 0.8,
      };

      const result =
        source === 'camera'
          ? await ImagePicker.launchCameraAsync(options)
          : await ImagePicker.launchImageLibraryAsync(options);

      if (result.canceled || !result.assets[0]) {
        return;
      }

      const pickedAsset = result.assets[0];
      if (!pickedAsset.uri) {
        showVideoErrorDialog(
          'Invalid video file',
          'This video file could not be processed. Please try a different video.'
        );
        return;
      }

      const fallback = {
        duration: pickedAsset.duration,
        width: pickedAsset.width,
        height: pickedAsset.height,
        fileSize: pickedAsset.fileSize,
      };

      let inspected;
      try {
        inspected = await inspectVideo(pickedAsset.uri, fallback);
      } catch (err) {
        console.warn('[CreateReferral] Unable to read video metadata:', err);
        showVideoErrorDialog(
          'Unable to read video',
          'This video file could not be processed. Please try a different video.'
        );
        return;
      }

      const durationCheck = validateVideoDuration(inspected.duration, { max: VIDEO_MAX_DURATION });
      if (!durationCheck.ok) {
        showVideoErrorDialog(durationCheck.title, durationCheck.message);
        return;
      }

      setIsProcessingVideo(true);
      setVideoProcessingProgress(0);

      const prepared = await prepareVideoForUpload({
        uri: pickedAsset.uri,
        maxDuration: VIDEO_MAX_DURATION,
        fallback: {
          duration: inspected.duration,
          width: inspected.width,
          height: inspected.height,
          fileSize: inspected.sizeBytes,
        },
        onProgress: (progress) => {
          if (isMountedRef.current) {
            setVideoProcessingProgress(progress);
          }
        },
        registerCancel: (cancel) => {
          cancelOptimizationRef.current = cancel;
        },
      });

      cancelOptimizationRef.current = null;

      if (!isMountedRef.current) {
        if (prepared.status === 'success' && prepared.wasOptimized) {
          await cleanupOptimizedVideo(prepared.uri);
        }
        return;
      }

      if (prepared.status === 'cancelled') {
        return;
      }

      if (prepared.status === 'error') {
        showVideoErrorDialog(prepared.title, prepared.message);
        return;
      }

      const previousTemp = optimizedTempUriRef.current;
      optimizedTempUriRef.current = prepared.wasOptimized ? prepared.uri : null;

      const durationLabel = formatDurationSeconds(prepared.optimized.duration);
      const sizeLabel = formatFileSize(prepared.optimized.sizeBytes);
      const metaParts = [durationLabel, sizeLabel].filter(Boolean);
      setVideoMetaLabel(metaParts.join(' · ') || null);
      setVideoUri(prepared.uri);

      if (previousTemp && previousTemp !== prepared.uri) {
        await cleanupOptimizedVideo(previousTemp);
      }
    } catch (err: any) {
      console.error('Error selecting video:', err);
      if (isMountedRef.current) {
        const errorMsg = err instanceof Error ? err.message.toLowerCase() : '';
        if (errorMsg.includes('permission')) {
          showVideoErrorDialog(
            'Permission needed',
            source === 'camera'
              ? 'Enable Camera (and Microphone) in Settings to record a video.'
              : 'Allow photo library access in Settings to choose a video.',
            Platform.OS === 'web'
              ? undefined
              : { label: 'Open Settings', onPress: () => Linking.openSettings() }
          );
        } else {
          showVideoErrorDialog(
            'Video selection failed',
            'We encountered an issue selecting your video. Please try again.'
          );
        }
      }
    } finally {
      if (isMountedRef.current) {
        setIsPickingVideo(false);
        setIsProcessingVideo(false);
      }
    }
  };

  const handleRemoveVideo = async () => {
    if (optimizedTempUriRef.current) {
      await cleanupOptimizedVideo(optimizedTempUriRef.current);
      optimizedTempUriRef.current = null;
    }
    setVideoUri(null);
    setExistingVideoUrl(null);
    setVideoMetaLabel(null);
  };

  const handleCancelProcessing = () => {
    if (cancelOptimizationRef.current) {
      cancelOptimizationRef.current();
      cancelOptimizationRef.current = null;
    }
    setIsProcessingVideo(false);
    setVideoProcessingProgress(0);
  };

  const handleSubmit = async () => {
    if (activeTab === 'text') {
      if (!businessName.trim()) {
        showErrorToast('Please enter business name');
        return;
      }
      if (!businessDetails.trim()) {
        showErrorToast('Please enter business details');
        return;
      }
      if (countWords(businessDetails) > 140) {
        showErrorToast('About Your Business must be 140 words or less');
        return;
      }
      if (!lookingFor.trim()) {
        showErrorToast('Please enter what you are looking for');
        return;
      }
      if (countWords(lookingFor) > 30) {
        showErrorToast('Looking For must be 30 words or less');
        return;
      }
    } else if (!hasVideoReady) {
      showErrorToast('Please record or upload a referral video');
      return;
    }

    try {
      setSubmitting(true);

      if (activeTab === 'video') {
        if (isEditMode && requestId) {
          if (hasLocalVideo && videoUri) {
            await updateReferralVideoRequest(requestId, videoUri);
          } else if (existingVideoUrl) {
            await updateReferralRequest(requestId, { videoUrl: existingVideoUrl });
          } else {
            showErrorToast('Please record or upload a referral video');
            return;
          }
          showSuccessToast('Referral request updated successfully!');
        } else {
          if (!videoUri) {
            showErrorToast('Please record or upload a referral video');
            return;
          }
          await createReferralVideoRequest(clubId, videoUri);
          showSuccessToast('Referral request posted successfully!');
        }

        if (optimizedTempUriRef.current) {
          await cleanupOptimizedVideo(optimizedTempUriRef.current);
          optimizedTempUriRef.current = null;
        }
      } else if (isEditMode && requestId) {
        await updateReferralRequest(requestId, {
          businessName: businessName.trim(),
          businessDetails: businessDetails.trim(),
          lookingFor: lookingFor.trim(),
        });
        showSuccessToast('Referral request updated successfully!');
      } else {
        await createReferralRequest({
          clubId,
          businessName: businessName.trim(),
          businessDetails: businessDetails.trim(),
          lookingFor: lookingFor.trim(),
        });
        showSuccessToast('Referral request posted successfully!');
      }
      navigation.goBack();
    } catch (error: any) {
      if (error.message?.includes('You can only post one referral per week')) {
        Alert.alert('Referral Limit Reached', error.message);
      } else {
        showErrorToast(error.message || `Failed to ${isEditMode ? 'update' : 'post'} referral request`);
      }
    } finally {
      setSubmitting(false);
    }
  };

  const isFormValid =
    activeTab === 'text'
      ? Boolean(businessName.trim()) &&
        Boolean(businessDetails.trim()) &&
        Boolean(lookingFor.trim()) &&
        countWords(businessDetails) <= 140 &&
        countWords(lookingFor) <= 30
      : hasVideoReady;

  return (
    <SafeAreaView style={[styles.container, { backgroundColor: theme.background }]} edges={['top', 'left', 'right']}>
      <View
        style={[styles.header, { backgroundColor: theme.card, borderBottomColor: theme.border }]}
        onLayout={(event) => {
          const { height } = event.nativeEvent.layout;
          setHeaderHeight(height);
        }}
      >
        <TouchableOpacity onPress={() => navigation.goBack()} style={styles.headerButton} activeOpacity={0.7}>
          <Ionicons name="arrow-back" size={28} color={theme.text} />
        </TouchableOpacity>
        <ThemedText style={[styles.headerTitle, { color: theme.text }]}>
          {isEditMode ? 'Edit Referral Request' : 'Post Referral Request'}
        </ThemedText>
        <View style={styles.headerButton} />
      </View>

      <KeyboardAvoidingView
        style={styles.flex}
        behavior={Platform.OS === 'ios' ? 'padding' : undefined}
        keyboardVerticalOffset={Platform.OS === 'ios' ? 90 : 0}
      >
        {loading ? (
          <View style={styles.loadingContainer}>
            <ActivityIndicator size="large" color={theme.accent} />
            <ThemedText style={[styles.loadingText, { color: theme.textSecondary }]}>
              Loading...
            </ThemedText>
          </View>
        ) : (
          <ScrollView
            style={styles.scrollView}
            contentContainerStyle={styles.scrollContent}
            keyboardShouldPersistTaps="handled"
          >
            <IntroSegmentedControl selectedTab={activeTab} onTabChange={setActiveTab} />

            {activeTab === 'text' ? (
              <>
                <View style={[styles.infoBanner, { backgroundColor: theme.accent + '15', borderColor: theme.accent + '30' }]}>
                  <Ionicons name="information-circle" size={20} color={theme.accent} />
                  <ThemedText style={[styles.infoText, { color: theme.accent }]}>
                    Post your referral request and let club members help you find the right connections!
                  </ThemedText>
                </View>

                <View style={styles.formGroup}>
                  <View style={styles.labelRow}>
                    <Ionicons name="business-outline" size={18} color={theme.accent} />
                    <ThemedText style={[styles.label, { color: theme.text }]}>
                      Business Name *
                    </ThemedText>
                  </View>
                  <TextInput
                    style={[styles.input, { backgroundColor: theme.card, borderColor: theme.border, color: theme.text }]}
                    placeholder="Enter business or company name"
                    placeholderTextColor={theme.textTertiary}
                    value={businessName}
                    onChangeText={setBusinessName}
                    maxLength={20}
                  />
                  <ThemedText style={[styles.hint, { color: theme.textTertiary }]}>
                    The name of the business you're representing
                  </ThemedText>
                </View>

                <View style={styles.formGroup}>
                  <View style={styles.labelRow}>
                    <Ionicons name="information-circle-outline" size={18} color={theme.accent} />
                    <ThemedText style={[styles.label, { color: theme.text }]}>
                      About Your Business *
                    </ThemedText>
                  </View>
                  <TextInput
                    style={[
                      styles.input,
                      styles.textArea,
                      { backgroundColor: theme.card, borderColor: theme.border, color: theme.text },
                    ]}
                    placeholder="Describe your business, what you do, your services or products..."
                    placeholderTextColor={theme.textTertiary}
                    value={businessDetails}
                    onChangeText={setBusinessDetails}
                    multiline
                    numberOfLines={5}
                    textAlignVertical="top"
                  />
                  <ThemedText style={[styles.hint, { color: countWords(businessDetails) > 140 ? theme.error || '#D32F2F' : theme.textTertiary }]}>
                    {countWords(businessDetails)}/140 words
                  </ThemedText>
                </View>

                <View style={styles.formGroup}>
                  <View style={styles.labelRow}>
                    <Ionicons name="search-outline" size={18} color={theme.accent} />
                    <ThemedText style={[styles.label, { color: theme.text }]}>
                      Looking For *
                    </ThemedText>
                  </View>
                  <TextInput
                    style={[
                      styles.input,
                      styles.textArea,
                      { backgroundColor: theme.card, borderColor: theme.border, color: theme.text },
                    ]}
                    placeholder="What kind of connection or introduction are you seeking? Be specific..."
                    placeholderTextColor={theme.textTertiary}
                    value={lookingFor}
                    onChangeText={setLookingFor}
                    multiline
                    numberOfLines={5}
                    textAlignVertical="top"
                  />
                  <ThemedText style={[styles.hint, { color: countWords(lookingFor) > 30 ? theme.error || '#D32F2F' : theme.textTertiary }]}>
                    {countWords(lookingFor)}/30 words
                  </ThemedText>
                </View>

                <View style={[styles.exampleBox, { backgroundColor: theme.card, borderColor: theme.border }]}>
                  <ThemedText style={[styles.exampleTitle, { color: theme.text }]}>
                    Example:
                  </ThemedText>
                  <ThemedText style={[styles.exampleText, { color: theme.textSecondary }]}>
                    <ThemedText style={{ fontWeight: '600' }}>Business:</ThemedText> XYZ Marketing Agency
                  </ThemedText>
                  <ThemedText style={[styles.exampleText, { color: theme.textSecondary }]}>
                    <ThemedText style={{ fontWeight: '600' }}>About:</ThemedText> We're a digital marketing agency specializing in social media management and content creation for small businesses.
                  </ThemedText>
                  <ThemedText style={[styles.exampleText, { color: theme.textSecondary }]}>
                    <ThemedText style={{ fontWeight: '600' }}>Looking For:</ThemedText> Introduction to business owners in the retail or hospitality sector who need help growing their online presence.
                  </ThemedText>
                </View>
              </>
            ) : (
              <View style={styles.formGroup}>
                {hasLocalVideo && videoUri ? (
                  <SelectedVideoPreview
                    uri={videoUri}
                    subtitle={videoMetaLabel || undefined}
                    onRemove={handleRemoveVideo}
                    muted={false}
                  />
                ) : existingVideoUrl ? (
                  <View>
                    <FeedVideoPlayer uri={existingVideoUrl} style={styles.existingVideo} />
                    <TouchableOpacity
                      style={[styles.replaceVideoButton, { borderColor: theme.border }]}
                      onPress={handleRemoveVideo}
                      activeOpacity={0.7}
                    >
                      <Ionicons name="trash-outline" size={18} color={theme.error} />
                      <ThemedText style={[styles.replaceVideoText, { color: theme.error }]}>
                        Remove video
                      </ThemedText>
                    </TouchableOpacity>
                  </View>
                ) : (
                  <TouchableOpacity
                    style={[
                      styles.videoUploadBox,
                      { backgroundColor: theme.card, borderColor: theme.border },
                    ]}
                    onPress={() => setShowVideoSourcePicker(true)}
                    activeOpacity={0.7}
                  >
                    <View style={[styles.videoIconCircle, { backgroundColor: theme.accent + '15' }]}>
                      <Ionicons name="videocam" size={36} color={theme.accent} />
                    </View>
                    <ThemedText style={[styles.videoUploadTitle, { color: theme.text }]}>
                      Record or Upload Video Request
                    </ThemedText>
                    <ThemedText style={[styles.videoUploadSubtitle, { color: theme.textSecondary }]}>
                      Record a short video (under {VIDEO_MAX_DURATION}s) describing what you are looking for. No form fields needed.
                    </ThemedText>

                    <View style={styles.videoActionButtons}>
                      <TouchableOpacity
                        style={[styles.videoActionButton, { backgroundColor: theme.accent }]}
                        onPress={() => handlePickVideo('camera')}
                        activeOpacity={0.8}
                      >
                        <Ionicons name="camera" size={18} color="#FFFFFF" />
                        <ThemedText style={styles.videoActionButtonText}>Record Video</ThemedText>
                      </TouchableOpacity>

                      <TouchableOpacity
                        style={[
                          styles.videoActionButtonSecondary,
                          { borderColor: theme.accent, backgroundColor: theme.backgroundSecondary },
                        ]}
                        onPress={() => handlePickVideo('library')}
                        activeOpacity={0.8}
                      >
                        <Ionicons name="images-outline" size={18} color={theme.accent} />
                        <ThemedText style={[styles.videoActionButtonTextSecondary, { color: theme.accent }]}>
                          Upload Video
                        </ThemedText>
                      </TouchableOpacity>
                    </View>
                  </TouchableOpacity>
                )}
              </View>
            )}

            <TouchableOpacity
              style={[
                styles.submitButton,
                { backgroundColor: isFormValid ? theme.accent : theme.border },
              ]}
              onPress={handleSubmit}
              disabled={!isFormValid || submitting || loading || isProcessingVideo}
              activeOpacity={0.7}
            >
              {submitting ? (
                <ActivityIndicator color={theme.textInverse} />
              ) : (
                <>
                  <Ionicons name="send" size={20} color={isFormValid ? theme.textInverse : theme.textTertiary} />
                  <ThemedText
                    style={[
                      styles.submitButtonText,
                      { color: isFormValid ? theme.textInverse : theme.textTertiary },
                    ]}
                  >
                    {isEditMode ? 'Update Request' : 'Post Request'}
                  </ThemedText>
                </>
              )}
            </TouchableOpacity>

            <FormFooterSpacer
              headerHeight={headerHeight}
              bottomInset={bottomInset}
              keyboardHeight={keyboardHeight}
            />
          </ScrollView>
        )}
      </KeyboardAvoidingView>

      <VideoSourcePicker
        visible={showVideoSourcePicker}
        onSelect={handlePickVideo}
        onCancel={() => setShowVideoSourcePicker(false)}
        maxDuration={VIDEO_MAX_DURATION}
      />

      <VideoProcessingDialog
        visible={isProcessingVideo}
        progress={videoProcessingProgress}
        onCancel={handleCancelProcessing}
      />

      <VideoErrorDialog
        visible={videoError.visible}
        title={videoError.title}
        message={videoError.message}
        actionLabel={videoError.actionLabel}
        onAction={videoError.onAction}
        onClose={() => setVideoError({ visible: false, title: '', message: '' })}
      />
    </SafeAreaView>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
  },
  flex: {
    flex: 1,
  },
  loadingContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    padding: 32,
  },
  loadingText: {
    fontSize: 14,
    marginTop: 12,
  },
  header: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingHorizontal: 16,
    paddingVertical: 12,
    borderBottomWidth: 1,
  },
  headerButton: {
    width: 40,
    height: 40,
    alignItems: 'center',
    justifyContent: 'center',
  },
  headerTitle: {
    fontSize: 18,
    fontWeight: '700',
  },
  scrollView: {
    flex: 1,
  },
  scrollContent: {
    padding: 16,
  },
  infoBanner: {
    flexDirection: 'row',
    alignItems: 'center',
    padding: 12,
    borderRadius: 8,
    marginBottom: 24,
    gap: 10,
    borderWidth: 1,
  },
  infoText: {
    flex: 1,
    fontSize: 13,
    lineHeight: 18,
  },
  formGroup: {
    marginBottom: 24,
  },
  labelRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
    marginBottom: 10,
  },
  label: {
    fontSize: 15,
    fontWeight: '600',
  },
  input: {
    borderWidth: 1,
    borderRadius: 10,
    paddingHorizontal: 16,
    paddingVertical: 12,
    fontSize: 15,
  },
  textArea: {
    minHeight: 120,
    paddingTop: 12,
  },
  hint: {
    fontSize: 12,
    marginTop: 6,
  },
  exampleBox: {
    borderWidth: 1,
    borderRadius: 10,
    padding: 16,
    marginTop: 8,
  },
  exampleTitle: {
    fontSize: 14,
    fontWeight: '700',
    marginBottom: 8,
  },
  exampleText: {
    fontSize: 13,
    lineHeight: 20,
    marginBottom: 6,
  },
  videoUploadBox: {
    borderWidth: 1.5,
    borderStyle: 'dashed',
    borderRadius: 16,
    padding: 24,
    alignItems: 'center',
    justifyContent: 'center',
  },
  videoIconCircle: {
    width: 64,
    height: 64,
    borderRadius: 32,
    alignItems: 'center',
    justifyContent: 'center',
    marginBottom: 12,
  },
  videoUploadTitle: {
    fontSize: 17,
    fontWeight: '700',
    marginBottom: 6,
    textAlign: 'center',
  },
  videoUploadSubtitle: {
    fontSize: 13,
    lineHeight: 18,
    textAlign: 'center',
    marginBottom: 20,
    paddingHorizontal: 12,
  },
  videoActionButtons: {
    flexDirection: 'row',
    gap: 12,
    width: '100%',
  },
  videoActionButton: {
    flex: 1,
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    paddingVertical: 12,
    borderRadius: 10,
    gap: 6,
  },
  videoActionButtonText: {
    color: '#FFFFFF',
    fontSize: 14,
    fontWeight: '600',
  },
  videoActionButtonSecondary: {
    flex: 1,
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    paddingVertical: 12,
    borderRadius: 10,
    borderWidth: 1,
    gap: 6,
  },
  videoActionButtonTextSecondary: {
    fontSize: 14,
    fontWeight: '600',
  },
  existingVideo: {
    width: '100%',
    aspectRatio: 1,
    borderRadius: 14,
    marginTop: 0,
  },
  replaceVideoButton: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 6,
    marginTop: 12,
    paddingVertical: 10,
    borderRadius: 8,
    borderWidth: 1,
  },
  replaceVideoText: {
    fontSize: 14,
    fontWeight: '600',
  },
  submitButton: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    paddingVertical: 16,
    borderRadius: 10,
    gap: 8,
    marginTop: 8,
  },
  submitButtonText: {
    fontSize: 16,
    fontWeight: '600',
  },
});

export default CreateReferralScreen;
